import styled from 'styled-components';
import * as yup from 'yup';
import { setIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useEnqueueStacks } from '@avangard/utils/hooks';
import {
    Button,
    DatepickerSinglePicker,
    DatepickerTextField,
    OnDateChangeProps,
    TextField,
} from '@avangard/ui/core';
import { MenuItem } from '@material-ui/core';

import { FormStyled } from '@modules/layout/styled';
import { LapPlatoonMask } from '@modules/layout/atoms';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { useCurrentUser } from '@modules/user/hooks';
import { PLATOONS_COMPANY_OPTIONS, programTypes } from '@modules/lap/constants';
import { useLapsTableFiltersQuery } from '@modules/lap/hooks';
import { CreateLapRequest, LapCollectionRequest } from '@modules/lap/requests';
import { CreateLapMutation, GetLapCollectionQuery, GetLapsQuery } from '@modules/lap/graphql';
import { UserGrantActionToken, UserGrantCategoryToken } from '@modules/types/graphql';

import type { CreateLapFormValues } from '@modules/lap/requests';
import type { CreateLapMutationType, CreateLapMutationVariables } from '@modules/types/graphql';

type LapCreateFormProps = {
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const platoonInputProps = {
    inputComponent: LapPlatoonMask,
};

const LapCreateForm = (props: LapCreateFormProps) => {
    const { onCancel } = props;

    const { t } = useTranslation(['common', 'errors', 'lap']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const { filters } = useLapsTableFiltersQuery();
    const args = new LapCollectionRequest(filters);

    const { currentUser } = useCurrentUser();
    const { lapGrants } = currentUser.can({
        [UserGrantCategoryToken.lap]: [UserGrantActionToken.list],
    });

    const [createLap] = useMutation<CreateLapMutationType, CreateLapMutationVariables>(
        CreateLapMutation,
    );

    const initialValues: CreateLapFormValues = {
        programType: '',
        from: null,
        to: null,
        platform: '',
        course: null,
        platoonsCompanyCount: '',
        platoonsByCompanies: [],
    };

    return (
        <ExtendedFormik<CreateLapFormValues>
            validationSchema={yup.object({
                programType: yup.string().required(t('errors:validations.required') ?? ''),
                from: yup
                    .date()
                    .nullable()
                    .required(t('errors:validations.required') ?? ''),
                to: yup
                    .date()
                    .nullable()
                    .required(t('errors:validations.required') ?? '')
                    .min(yup.ref('from'), t('errors:time.invalid_range') ?? ''),
                platoonsCompanyCount: yup
                    .number()
                    .nullable()
                    .required(t('errors:validations.required') ?? ''),
            })}
            initialValues={initialValues}
            onSubmit={async values => {
                try {
                    let options: Parameters<typeof createLap>[0] = {
                        variables: { item: new CreateLapRequest(values) },
                    };

                    if (lapGrants.canList) {
                        options = {
                            ...options,
                            refetchQueries: [
                                { query: GetLapCollectionQuery, variables: { args } },
                                { query: GetLapsQuery },
                            ],
                            awaitRefetchQueries: true,
                        };
                    }

                    const { data: createLapData } = await createLap(options);

                    const lapId = createLapData?.createLap?.id;

                    if (lapId) {
                        enqueueSuccess(t('lap:notifiers.add_lap.success'));
                        onCancel();
                    } else {
                        enqueueError(t('lap:notifiers.add_lap.error'));
                    }
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { values, errors, isSubmitting, setFieldValue, setValues, handleChange } =
                    formikProps;

                const handleChangeDate = (field: string) => (data: OnDateChangeProps) =>
                    setFieldValue(field, data.date);

                const handleChangePlatoonsCompanyCount = (
                    event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                    const companyCount = +event.target.value;

                    setFieldValue('platoonsCompanyCount', companyCount);

                    let updatedPlatoonsByCompanies = [...values.platoonsByCompanies];

                    if (companyCount > updatedPlatoonsByCompanies.length) {
                        for (let i = updatedPlatoonsByCompanies.length; i < companyCount; i++) {
                            updatedPlatoonsByCompanies.push([i + 1, '']);
                        }
                    } else if (companyCount < updatedPlatoonsByCompanies.length) {
                        updatedPlatoonsByCompanies = updatedPlatoonsByCompanies.slice(
                            0,
                            companyCount,
                        );
                    }

                    setFieldValue('platoonsByCompanies', updatedPlatoonsByCompanies);
                };

                const handleChangePlatoonsByCompaniesItem =
                    (company: number, index: number) =>
                    (event: React.ChangeEvent<HTMLInputElement>) => {
                        const platoonCount = event.target.value;

                        setValues(values => {
                            const field = `platoonsByCompanies[${index}]`;

                            return setIn(values, field, [company, platoonCount]);
                        });
                    };

                return (
                    <StyledForm>
                        <DatepickerSinglePicker
                            disableToday
                            date={values.from}
                            onDateChange={handleChangeDate('from')}
                        >
                            {datepickerProps => {
                                const { inputProps, inputComponent } = datepickerProps;

                                return (
                                    <DatepickerTextField
                                        required
                                        id='from'
                                        label={t('common:forms.labels.start_lap_date')}
                                        placeholder={t('common:forms.placeholder.date') ?? ''}
                                        error={!!errors.from}
                                        helperText={errors.from}
                                        InputProps={{ inputComponent }}
                                        {...inputProps}
                                    />
                                );
                            }}
                        </DatepickerSinglePicker>

                        <DatepickerSinglePicker
                            disableToday
                            date={values.to}
                            onDateChange={handleChangeDate('to')}
                        >
                            {datepickerProps => {
                                const { inputProps, inputComponent } = datepickerProps;

                                return (
                                    <DatepickerTextField
                                        required
                                        id='to'
                                        label={t('common:forms.labels.end_lap_date')}
                                        placeholder={t('common:forms.placeholder.date') ?? ''}
                                        error={!!errors.to}
                                        helperText={errors.to}
                                        InputProps={{ inputComponent }}
                                        {...inputProps}
                                    />
                                );
                            }}
                        </DatepickerSinglePicker>

                        <TextField
                            select
                            required
                            fullWidth
                            id='programType'
                            name='programType'
                            label={t('common:forms.labels.program_type')}
                            placeholder={t('common:forms.placeholder.choose_program_type') ?? ''}
                            value={values.programType}
                            error={!!errors.programType}
                            helperText={errors.programType}
                            onChange={handleChange}
                        >
                            <MenuItem disabled value=''>
                                {t('common:forms.placeholder.choose_program_type')}
                            </MenuItem>

                            {programTypes.map(programType => (
                                <MenuItem key={programType.key} value={programType.key}>
                                    {t(programType.value)}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            required
                            fullWidth
                            id='platoonsCompanyCount'
                            name='platoonsCompanyCount'
                            label={t('lap:form.placeholder.typing_company_count')}
                            placeholder={t('lap:form.placeholder.typing_company_count') ?? ''}
                            value={values.platoonsCompanyCount}
                            onChange={handleChangePlatoonsCompanyCount}
                        >
                            <MenuItem disabled value=''>
                                {t('common:stubs.not_chosen')}
                            </MenuItem>

                            {PLATOONS_COMPANY_OPTIONS.map(companyOption => (
                                <MenuItem key={companyOption} value={companyOption}>
                                    {companyOption}
                                </MenuItem>
                            ))}
                        </TextField>

                        {values.platoonsByCompanies.map(([company, platoon], idx) => (
                            <TextField
                                required
                                fullWidth
                                key={idx}
                                label={t(
                                    'lap:form.placeholder.typing_platoon_count_in_company_count',
                                    { company },
                                )}
                                placeholder={
                                    t('lap:form.placeholder.typing_platoon_count_in_company') ?? ''
                                }
                                value={platoon}
                                inputProps={{ min: 1, max: 8 }}
                                InputProps={platoonInputProps}
                                onChange={handleChangePlatoonsByCompaniesItem(company, idx)}
                            />
                        ))}

                        <FormRowSubmit>
                            <Button variant='outlined' disabled={isSubmitting} onClick={onCancel}>
                                {t('common:actions.cancel')}
                            </Button>

                            <Button type='submit' loading={isSubmitting}>
                                {t('common:actions.add')}
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { LapCreateForm };
